/**
 * EventFamilyMembersRow
 *
 * Family member selection with color-coded chips.
 * Multi-select enabled for assigning events to family members.
 */

import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import type { FamilyMember } from '@/types/family-member';

type EventFamilyMembersRowProps = {
  familyMembers: FamilyMember[];
  selectedMemberIds: string[];
  onToggleMember: (memberId: string) => void;
  palette: typeof Colors.light;
};

export function EventFamilyMembersRow({
  familyMembers,
  selectedMemberIds,
  onToggleMember,
  palette,
}: EventFamilyMembersRowProps) {
  if (familyMembers.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        <Ionicons name="pricetag-outline" size={18} color={palette.textMuted} />
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.chipsContainer}
      >
        {familyMembers.map(member => {
          const isSelected = selectedMemberIds.includes(member.id);
          return (
            <TouchableOpacity
              key={member.id}
              style={[
                styles.chip,
                {
                  backgroundColor: isSelected ? member.color : 'transparent',
                  borderColor: member.color,
                },
              ]}
              onPress={() => onToggleMember(member.id)}
              activeOpacity={0.7}
            >
              <View
                style={[
                  styles.colorDot,
                  {
                    backgroundColor: isSelected ? '#FFFFFF' : member.color,
                  },
                ]}
              />
              <Text
                style={[
                  styles.chipText,
                  {
                    color: isSelected ? '#FFFFFF' : palette.text,
                    fontFamily: getBodyFont(),
                  },
                ]}
                numberOfLines={1}
              >
                {member.name}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm + 2, // 10px
  },
  iconContainer: {
    width: 40,
    alignItems: 'center',
  },
  chipsContainer: {
    flexDirection: 'row',
    gap: Spacing.sm,
    paddingRight: Spacing.lg,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Spacing.sm, // normalized from 6
    paddingHorizontal: Spacing.md,
    borderRadius: BorderRadius.lg,
    borderWidth: 1.5,
    gap: Spacing.sm, // normalized from 6
  },
  colorDot: {
    width: 8,
    height: 8,
    borderRadius: 4, // circular
  },
  chipText: {
    fontSize: 14,
    fontWeight: '500',
  },
});
